// ── Messages.js — unified inbox (romantic + friendly) ────────────────────
// Option B: single inbox, two visual contexts
//   Romantic conversations: gold ♥ badge — born from a mutual like
//   Friendly conversations: teal ✦ badge — born from BeeConnect
// Both contexts use the same Realtime subscription, same DB table.

import { useState, useRef, useEffect } from 'react';
import { useTr } from '../utils/useTr';
import { useApp } from '../contexts/AppContext';
import { canAccess } from '../utils/monetization';
import { generateIcebreakers } from '../utils/icebreakers';
import { compatLabel, computeRadarCompat } from '../utils/matching';
import DateCheckinModal from '../components/DateCheckinModal';
import { getMoodById } from '../utils/beeJourney';
import ProfileSheet from '../components/ProfileSheet';

// ── Context badge ─────────────────────────────────────────────────────────
function ContextBadge({ mode, compact = false }) {
  const isRomantic = mode !== 'friendly';
  return compact ? (
    <span style={{
      display:'inline-flex', alignItems:'center', justifyContent:'center',
      width:16, height:16, borderRadius:'50%', fontSize:9, flexShrink:0,
      background: isRomantic ? 'rgba(201,168,76,0.2)' : 'rgba(29,158,117,0.15)',
      color:       isRomantic ? 'var(--gold)' : '#1D9E75',
      border:      isRomantic ? '1px solid rgba(201,168,76,0.4)' : '1px solid rgba(29,158,117,0.3)',
    }}>
      {isRomantic ? '♥' : '✦'}
    </span>
  ) : (
    <span style={{
      fontSize:'0.58rem', padding:'1px 6px', borderRadius:99, fontWeight:600,
      background: isRomantic ? 'rgba(201,168,76,0.12)' : 'rgba(29,158,117,0.1)',
      color:       isRomantic ? 'var(--gold)' : '#1D9E75',
      border:      isRomantic ? '1px solid rgba(201,168,76,0.3)' : '1px solid rgba(29,158,117,0.25)',
    }}>
      {isRomantic ? '♥ Match' : '✦ Connect'}
    </span>
  );
}

// ── Chat view ─────────────────────────────────────────────────────────────
function ChatView({ partner, conv, onBack }) {
  const tr  = useTr();
  const { activeMessages, currentUser, lang, featureMatrix, features, subsEnabled, sendMessage, markConversationRead, setActiveChat, getCompatibility } = useApp();
  const [input,       setInput]       = useState('');
  const [showIce,     setShowIce]     = useState(true);
  const [usedIce,     setUsedIce]     = useState(new Set());
  const [showProfile, setShowProfile] = useState(false);
  const [showCheckin, setShowCheckin] = useState(false);
  const endRef   = useRef(null);
  const inputRef = useRef(null);

  const mode = conv?.mode || 'romantic';
  const now  = Date.now();
  const msgs = (conv?.id
    ? (activeMessages || []).filter(m => m.conversation_id === conv.id)
    : (activeMessages || []))
    // Feature M — hide expired whispers from the UI (rows kept in DB for moderation)
    .filter(m => !m.expires_at || new Date(m.expires_at).getTime() > now);
  const isReceived = msgs.some(m => m.sender_id !== currentUser?.id);

  // Feature M — show whisper toggle only when conversation has 0 messages yet
  const whisperEnabled = features?.feature_whisper === true;
  const isFirstMessage = msgs.length === 0;
  const [whisperMode, setWhisperMode] = useState(false);
  useEffect(() => { if (!isFirstMessage) setWhisperMode(false); }, [isFirstMessage]);

  const baseIcebreakers = mode === 'friendly'
    ? [ 'What are you working on that excites you?', 'What did you do last weekend?',
        'Best local spot you\'d recommend?', 'What skill are you learning?' ]
    : (currentUser && partner ? generateIcebreakers(currentUser, partner, 3, lang) : []);

  // Axis-based icebreakers for romantic mode: surface questions from compat dimensions
  const AXIS_ICE = lang === 'fr' ? [
    { text: 'Quand tu traverses une période difficile, tu préfères en parler ou prendre du recul ?', axisLabel: 'Repair', axisColor: '#7AB8C4' },
    { text: 'Dans un désaccord, tu exprimes d\'abord ton ressenti ou tu cherches une solution ?', axisLabel: 'Conflict', axisColor: '#C4847A' },
    { text: 'Comment tu te comportes quand tu as fait une erreur avec quelqu\'un ?', axisLabel: 'Accountability', axisColor: '#9CA880' },
    { text: 'Tu donnes plus de ton temps, ton énergie ou ton attention ?', axisLabel: 'Generosity', axisColor: '#C9A84C' },
    { text: 'Dans une conversation importante, tu regardes ton téléphone ou tu déconnectes tout ?', axisLabel: 'Presence', axisColor: '#A084C4' },
  ] : [
    { text: 'When you\'re going through something hard, do you prefer to talk about it or take space?', axisLabel: 'Repair', axisColor: '#7AB8C4' },
    { text: 'In a disagreement, do you lead with feelings or solutions?', axisLabel: 'Conflict', axisColor: '#C4847A' },
    { text: 'How do you typically handle it when you\'ve made a mistake with someone?', axisLabel: 'Accountability', axisColor: '#9CA880' },
    { text: 'Do you give more of your time, energy, or attention?', axisLabel: 'Generosity', axisColor: '#C9A84C' },
    { text: 'During an important conversation, do you put your phone away?', axisLabel: 'Presence', axisColor: '#A084C4' },
  ];

  const axisIcebreakers = (() => {
    if (mode !== 'romantic' || !partner) return [];
    // Pick 2 questions seeded by partner id hash to keep consistent per conversation
    const seed = (partner.id || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
    return [AXIS_ICE[seed % AXIS_ICE.length], AXIS_ICE[(seed + 2) % AXIS_ICE.length]];
  })();

  const icebreakers = [...baseIcebreakers, ...axisIcebreakers];

  useEffect(() => {
    setShowIce(true);
    setUsedIce(new Set());
  }, [conv?.id]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior:'smooth' });
    if (conv?.id && currentUser?.id) {
      markConversationRead?.(conv.id, currentUser.id);
      import('../utils/supabase').then(({ supabase }) =>
        supabase?.rpc('mark_messages_seen', {
          p_conversation_id: conv.id, p_reader_id: currentUser.id,
        })
      );
    }
  }, [msgs.length, conv?.id]);

  const send = async () => {
    const text = input.trim();
    if (!text || !partner?.id) return;
    setInput('');
    setShowIce(false);
    const opts = (whisperEnabled && whisperMode && isFirstMessage) ? { whisper: true } : undefined;
    await sendMessage?.(partner.id, text, opts);
    setWhisperMode(false);
  };

  const COFFEE_TEXT = lang === 'fr'
    ? `☕ Invitation café — je serais ravi(e) de se retrouver autour d'un café. Tu es dispo ?`
    : `☕ Coffee invite — I'd love to grab a coffee with you. Are you up for it?`;

  const sendCoffeeInvite = async () => {
    if (!partner?.id) return;
    setShowIce(false);
    await sendMessage?.(partner.id, COFFEE_TEXT, { type: 'coffee_invite' });
  };

  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100%' }}>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', gap:'0.7rem', padding:'0.8rem 1rem', background:'var(--dark)', borderBottom:'1px solid var(--border)', flexShrink:0 }}>
        <button onClick={onBack} style={{ background:'none', border:'none', color:'var(--muted)', cursor:'pointer', fontSize:'1.1rem', padding:0, lineHeight:1 }}>←</button>
        <div style={{ width:36, height:36, borderRadius:'50%', overflow:'hidden', background:'var(--charcoal)', flexShrink:0 }}>
          {partner?.photos?.[0]
            ? <img src={partner.photos[0]} alt={partner.name} style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
            : <div style={{ width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:"'Cormorant Garamond',serif", fontSize:'1rem', color:'var(--gold)', opacity:0.5 }}>{(partner?.name||'?')[0]}</div>}
        </div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:'0.88rem', fontWeight:600, color:'var(--white)' }}>{partner?.name}</div>
          <div style={{ fontSize:'0.65rem', color:'var(--muted)' }}>
            {mode === 'friendly' ? (tr('beeconnect.connection') || 'Connection') : (compatLabel?.(partner?._compat) || '')}
          </div>
        </div>
        {features?.feature_date_checkin === true && mode === 'romantic' && (
          <button
            onClick={() => setShowCheckin(true)}
            title={lang === 'fr' ? 'Programmer un check-in date' : 'Schedule a date check-in'}
            style={{ background:'none', border:'none', color:'var(--gold)', cursor:'pointer', padding:4, lineHeight:1, flexShrink:0, fontSize:16 }}
          >🛡</button>
        )}
        <button
          onClick={() => setShowProfile(true)}
          title={lang === 'fr' ? 'Voir le profil' : 'View profile'}
          style={{ background:'none', border:'none', color:'var(--muted)', cursor:'pointer', padding:4, lineHeight:1, flexShrink:0 }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="8" r="4"/>
            <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
          </svg>
        </button>
        <span style={{
          width:28, height:28, borderRadius:'50%', flexShrink:0,
          display:'flex', alignItems:'center', justifyContent:'center', fontSize:13,
          background: mode !== 'friendly' ? 'rgba(201,168,76,0.15)' : 'rgba(29,158,117,0.12)',
          color:      mode !== 'friendly' ? '#C9A84C'               : '#1D9E75',
          border:     mode !== 'friendly' ? '1.5px solid rgba(201,168,76,0.45)' : '1.5px solid rgba(29,158,117,0.35)',
        }}>
          {mode !== 'friendly' ? '♥' : '✦'}
        </span>
      </div>
      {showProfile && partner && <ProfileSheet user={partner} onClose={() => setShowProfile(false)} preview/>}
      {showCheckin && partner && <DateCheckinModal partner={partner} matchId={conv?.match_id || null} onClose={() => setShowCheckin(false)}/>}

      {/* Messages */}
      <div style={{ flex:1, overflowY:'auto', padding:'1rem', display:'flex', flexDirection:'column', gap:'0.5rem' }}>
        {showIce && icebreakers?.length > 0 && msgs.length === 0 && (
          <div style={{ marginBottom:'0.5rem' }}>
            <p style={{ fontSize:'0.62rem', color:'var(--muted)', letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:'0.5rem' }}>
              {tr('messages.icebreakers') || 'Suggestions'}
            </p>
            <div style={{ display:'flex', flexDirection:'column', gap:'0.25rem' }}>
              {icebreakers.slice(0, 5).filter((_, i) => !usedIce.has(i)).map((q, i) => {
                const text    = q.text ?? q;
                const isAxis  = !!q.axisLabel;
                const concept = isAxis
                  ? q.axisLabel
                  : ({ interest:'En commun', mbti:'Perso', language:'Langue', prompt:'Profil', universal:'Question' }[q.type] || 'Question');
                const axisColor = isAxis ? q.axisColor : null;
                return (
                  <button key={i} onClick={() => { setInput(text); inputRef.current?.focus(); setUsedIce(prev => new Set([...prev, i])); }}
                    style={{
                      display:'grid', gridTemplateColumns:'72px 1fr', alignItems:'center', gap:'0.6rem',
                      width:'100%', textAlign:'left', padding:'0.4rem 0.6rem',
                      background:'transparent', border:'none',
                      borderRadius:'var(--r-md)', cursor:'pointer', fontFamily:'Outfit',
                    }}>
                    <span style={{
                      fontSize:'0.52rem', fontWeight:700, letterSpacing:'0.05em', textTransform:'uppercase',
                      padding:'2px 0', textAlign:'right',
                      color: axisColor || 'var(--muted)',
                      whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis',
                    }}>
                      {concept}
                    </span>
                    <span style={{ fontSize:'0.76rem', color:'var(--text)', lineHeight:1.35 }}>
                      {text}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {msgs.map((m, i) => {
          const isMe   = m.sender_id === currentUser?.id;
          const isSeen = !!m.seen_at;
          const hasReceipts = features?.perk_read_receipts !== false && (!subsEnabled || canAccess(currentUser, 'read_receipts', featureMatrix));
          const isCoffee = m.type === 'coffee_invite' || (m.text || '').startsWith('☕');
          if (isCoffee) {
            return (
              <div key={m.id || i} style={{ display:'flex', justifyContent: isMe ? 'flex-end' : 'flex-start' }}>
                <div style={{ maxWidth:'80%', padding:'0.75rem 1rem', background: isMe ? 'rgba(201,168,76,0.12)' : 'rgba(201,168,76,0.06)', border:'1px solid rgba(201,168,76,0.35)', borderRadius:'var(--r-md)', textAlign:'center' }}>
                  <div style={{ fontSize:'1.5rem', marginBottom:'0.25rem' }}>☕</div>
                  <p style={{ fontSize:'0.78rem', color:'var(--gold)', fontWeight:600, margin:'0 0 4px' }}>
                    {lang === 'fr' ? (isMe ? 'Vous avez envoyé une invitation café' : `${partner?.name?.split(' ')[0]} vous invite à un café`) : (isMe ? 'You sent a coffee invite' : `${partner?.name?.split(' ')[0]} invited you for coffee`)}
                  </p>
                  <p style={{ fontSize:'0.65rem', color:'var(--muted)', margin:0, lineHeight:1.4 }}>
                    {lang === 'fr' ? 'Un moment simple pour se retrouver' : 'A simple moment to meet up'}
                  </p>
                  <div style={{ fontSize:'0.55rem', color:'var(--muted)', marginTop:'0.4rem', opacity:0.5 }}>
                    {m.created_at ? new Date(m.created_at).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'}) : ''}
                  </div>
                </div>
              </div>
            );
          }
          // Feature M — Whisper styling: italic + ghost border
          const isWhisper = m.is_whisper === true || (!!m.expires_at && new Date(m.expires_at).getTime() > now);
          const whisperStyle = isWhisper ? {
            background: isMe ? 'rgba(201,168,76,0.18)' : 'rgba(255,255,255,0.06)',
            color:      isMe ? 'var(--gold)' : 'var(--muted)',
            border:     '1px dashed rgba(201,168,76,0.45)',
            fontStyle:  'italic',
          } : {};
          const expiresInH = isWhisper && m.expires_at
            ? Math.max(0, Math.round((new Date(m.expires_at).getTime() - now) / 3600000))
            : null;
          return (
            <div key={m.id || i} style={{ display:'flex', justifyContent: isMe ? 'flex-end' : 'flex-start' }}>
              <div style={{ maxWidth:'72%', padding:'0.55rem 0.85rem',
                background: isMe
                  ? (mode === 'friendly' ? 'linear-gradient(135deg,#1D9E75,#15785A)' : 'linear-gradient(135deg,var(--gold),#B8922A)')
                  : 'var(--dark)',
                color: isMe ? (mode === 'friendly' ? '#fff' : 'var(--black)') : 'var(--white)',
                fontSize:'0.85rem', lineHeight:1.5,
                border: isMe ? 'none' : '1px solid var(--border)',
                borderRadius: isMe ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                ...whisperStyle,
              }}>
                {m.text}
                <div style={{ fontSize:'0.58rem', marginTop:'0.2rem', opacity:0.6, display:'flex', alignItems:'center', gap:'0.25rem', justifyContent: isMe ? 'flex-end' : 'flex-start' }}>
                  {m.created_at ? new Date(m.created_at).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'}) : ''}
                  {isWhisper && expiresInH !== null && (
                    <span title={lang === 'fr' ? 'Whisper — disparaît bientôt' : 'Whisper — vanishes soon'} style={{ marginLeft: 4 }}>
                      💨 {expiresInH}h
                    </span>
                  )}
                  {isMe && hasReceipts && !isWhisper && (
                    <span style={{ color: isSeen ? (mode === 'friendly' ? '#4EC994' : 'var(--gold)') : 'inherit' }}>
                      {isSeen ? '✓✓' : '✓'}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={endRef}/>
      </div>

      {/* Input */}
      <div style={{ padding:'0.75rem 1rem', borderTop:'1px solid var(--border)', display:'flex', flexDirection:'column', gap:'0.4rem', background:'var(--dark)', flexShrink:0 }}>
        <div style={{ display:'flex', gap:'0.5rem' }}>
          <input
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && send()}
            placeholder={tr('messages.placeholder') || 'Write a message…'}
            style={{ flex:1, padding:'0.65rem 0.85rem', background:'rgba(255,255,255,0.05)', border:'1px solid var(--border)', borderRadius:'var(--r-pill)', color:'var(--white)', fontFamily:'Outfit', fontSize:'0.85rem', outline:'none' }}
          />
          <button onClick={send} disabled={!input.trim()}
            style={{ width:40, height:40, borderRadius:'50%', border:'none', cursor:'pointer', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center',
              background: !input.trim() ? 'rgba(255,255,255,0.05)' : (mode === 'friendly' ? 'var(--green)' : 'var(--gold)'),
              color: !input.trim() ? 'var(--muted)' : (mode === 'friendly' ? '#fff' : 'var(--black)'),
              transition:'all 0.15s',
            }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/></svg>
          </button>
        </div>
        <div style={{ display:'flex', gap:'0.4rem', flexWrap:'wrap' }}>
          {mode === 'romantic' && (
            <button
              onClick={sendCoffeeInvite}
              style={{ display:'flex', alignItems:'center', gap:'0.35rem', padding:'0.3rem 0.8rem', background:'rgba(201,168,76,0.08)', border:'1px solid rgba(201,168,76,0.3)', borderRadius:99, color:'var(--gold)', fontSize:'0.68rem', cursor:'pointer', fontFamily:'Outfit', letterSpacing:'0.04em' }}
            >
              ☕ {lang === 'fr' ? 'Inviter à un café' : 'Coffee invite'}
            </button>
          )}
          {whisperEnabled && isFirstMessage && (
            <button
              onClick={() => setWhisperMode(v => !v)}
              title={lang === 'fr' ? 'Le 1er message disparaîtra dans 24h' : 'First message vanishes in 24h'}
              style={{
                display:'flex', alignItems:'center', gap:'0.35rem', padding:'0.3rem 0.8rem',
                background: whisperMode ? 'rgba(201,168,76,0.18)' : 'rgba(255,255,255,0.04)',
                border: `1px ${whisperMode ? 'solid' : 'dashed'} rgba(201,168,76,${whisperMode ? '0.5' : '0.3'})`,
                borderRadius:99, color: whisperMode ? 'var(--gold)' : 'var(--muted)',
                fontSize:'0.68rem', cursor:'pointer', fontFamily:'Outfit', letterSpacing:'0.04em',
              }}
            >
              💨 {whisperMode
                ? (lang === 'fr' ? 'Whisper actif — 24h' : 'Whisper on — 24h')
                : (lang === 'fr' ? 'Whisper (24h)' : 'Whisper (24h)')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Conversation list item ─────────────────────────────────────────────────
function ConvRow({ partner, conv, unread, onClick }) {
  const isRomantic = conv?.mode !== 'friendly';
  return (
    <div onClick={onClick} style={{
      display:'flex', alignItems:'center', gap:'0.75rem',
      padding:'0.8rem 1rem', cursor:'pointer', transition:'background 0.1s',
      borderBottom:'1px solid var(--border)',
      background: unread > 0 ? 'rgba(255,255,255,0.02)' : 'transparent',
    }}
      onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.04)'}
      onMouseLeave={e => e.currentTarget.style.background = unread > 0 ? 'rgba(255,255,255,0.02)' : 'transparent'}
    >
      {/* Avatar */}
      <div style={{ position:'relative', flexShrink:0 }}>
        <div style={{ width:46, height:46, borderRadius:'50%', overflow:'hidden', background:'var(--charcoal)' }}>
          {partner?.photos?.[0]
            ? <img src={partner.photos[0]} alt={partner.name} style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
            : <div style={{ width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:"'Cormorant Garamond',serif", fontSize:'1.2rem', color:'var(--gold)', opacity:0.4 }}>{(partner?.name||'?')[0]}</div>}
        </div>
        {/* Context dot bottom-right */}
        <div style={{
          position:'absolute', bottom:0, right:0,
          width:16, height:16, borderRadius:'50%',
          background: isRomantic ? 'rgba(201,168,76,0.75)' : 'rgba(29,158,117,0.7)',
          border:'2px solid var(--black)',
          display:'flex', alignItems:'center', justifyContent:'center',
          fontSize:'7px', color: isRomantic ? '#0A0A0A' : '#fff',
        }}>
          {isRomantic ? '♥' : '✦'}
        </div>
      </div>

      {/* Content */}
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:'0.1rem' }}>
          <span style={{ fontSize:'0.88rem', fontWeight: unread > 0 ? 600 : 400, color:'var(--white)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
            {partner?.name}
          </span>
          <span style={{ fontSize:'0.62rem', color:'var(--muted)', flexShrink:0, marginLeft:8 }}>
            {conv?.last_message_at ? new Date(conv.last_message_at).toLocaleDateString([], {month:'short',day:'numeric'}) : ''}
          </span>
        </div>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <span style={{ fontSize:'0.75rem', color: unread > 0 ? 'var(--white)' : 'var(--muted)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', flex:1 }}>
            {conv?.last_message || (isRomantic ? '♥ New match' : '✦ New connection')}
          </span>
          {unread > 0 && (
            <span style={{ minWidth:18, height:18, borderRadius:99, background: isRomantic ? 'var(--gold-pale)' : 'var(--green-pale)', border: isRomantic ? '1px solid var(--gold-border)' : '1px solid var(--green-border)', color: isRomantic ? 'var(--gold)' : 'var(--green)', fontSize:'0.62rem', fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, marginLeft:8 }}>
              {unread}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ── ReferralSuggestions — profiles suggested by friends ──────────────────
const CARD_W = 155;
const CARD_H = 220;
const CARD_GAP = 8;

const COMPAT_COLOR = (s) => {
  if (s >= 90) return '#C9A84C';
  if (s >= 75) return '#9CA880';
  if (s >= 60) return '#7AB8C4';
  if (s >= 45) return '#888880';
  return '#C0453A';
};

export function ReferralSuggestions({ lang }) {
  const { currentUser, likeUser, setActiveChat, setPage } = useApp();
  const [referrals,   setReferrals]   = useState([]);
  const [previewUser, setPreviewUser] = useState(null);
  const [liked,       setLiked]       = useState(new Set());
  const [idx,         setIdx]         = useState(0);
  const [compats,     setCompats]     = useState({});

  useEffect(() => {
    if (!currentUser?.id) return;
    import('../utils/supabase').then(async ({ supabase }) => {
      const { data: refs } = await supabase
        .from('referrals')
        .select('id, message, created_at, profile_id, referrer_id')
        .eq('referred_id', currentUser.id)
        .is('seen_at', null)
        .order('created_at', { ascending: false });

      if (!refs?.length) return;

      const ids = [...new Set([...refs.map(r => r.profile_id), ...refs.map(r => r.referrer_id)])];
      const { data: usersData } = await supabase
        .from('users')
        .select('id, name, age, photos, job, criteria_scores, criteria_scores_fit')
        .in('id', ids);

      const byId = Object.fromEntries((usersData || []).map(u => [u.id, u]));
      const loaded = refs.map(r => ({
        ...r,
        profile:  byId[r.profile_id]  || null,
        referrer: byId[r.referrer_id] || null,
      }));
      setReferrals(loaded);

      // Compute compat scores locally — no dependency on candidates list
      const mySelf = currentUser?.criteria_scores     || {};
      const myNeed = currentUser?.criteria_scores_fit || {};
      const scores = {};
      loaded.forEach(r => {
        const p = byId[r.profile_id];
        if (!p) return;
        const s1 = computeRadarCompat(mySelf, p.criteria_scores_fit || {});
        const s2 = computeRadarCompat(myNeed, p.criteria_scores     || {});
        const fb = computeRadarCompat(mySelf, p.criteria_scores     || {});
        const both = s1 != null && s2 != null;
        const score = both ? Math.round((s1 + s2) / 2) : (s1 ?? s2 ?? fb);
        if (score != null) scores[r.profile_id] = Math.round(score);
      });
      setCompats(scores);
    });
  }, [currentUser?.id]);

  const dismiss = async (id) => {
    setReferrals(prev => {
      const next = prev.filter(r => r.id !== id);
      if (idx >= next.length && idx > 0) setIdx(idx - 1);
      return next;
    });
    const { supabase } = await import('../utils/supabase');
    supabase.from('referrals').update({ seen_at: new Date().toISOString() }).eq('id', id);
  };

  if (!referrals.length) return null;

  const fr = lang === 'fr';
  const STEP = 2;
  const canPrev = idx > 0;
  const canNext = idx < referrals.length - 1;

  const NavBtn = ({ dir }) => (
    <button
      onClick={() => setIdx(i => Math.min(Math.max(0, i + dir * STEP), referrals.length - 1))}
      disabled={dir === -1 ? !canPrev : !canNext}
      style={{
        position:'absolute', top:'50%', transform:'translateY(-50%)',
        [dir === -1 ? 'left' : 'right']: -14,
        zIndex:3, width:28, height:28, borderRadius:'50%',
        border:'1px solid var(--border)', background:'var(--surface)',
        color: (dir === -1 ? !canPrev : !canNext) ? 'var(--border)' : 'var(--text)',
        cursor: (dir === -1 ? !canPrev : !canNext) ? 'default' : 'pointer',
        display:'flex', alignItems:'center', justifyContent:'center',
        fontSize:16, opacity: (dir === -1 ? !canPrev : !canNext) ? 0.25 : 1,
        transition:'opacity 0.15s', boxShadow:'0 1px 4px rgba(0,0,0,0.35)',
        padding:0,
      }}
    >
      {dir === -1 ? '‹' : '›'}
    </button>
  );

  return (
    <div style={{ padding:'0.9rem 1rem 0.6rem', borderBottom:'1px solid var(--border)' }}>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'0.65rem' }}>
        <p style={{ margin:0, fontSize:'0.62rem', fontWeight:700, letterSpacing:'0.12em', color:'var(--gold)', textTransform:'uppercase' }}>
          {fr ? "✦ Suggestions d'amis" : '✦ Suggested by friends'}
        </p>
        <span style={{ fontSize:'0.58rem', color:'var(--muted)' }}>
          {idx + 1} / {referrals.length}
        </span>
      </div>

      {/* Carousel */}
      <div style={{ position:'relative', margin:'0 16px' }}>
        <NavBtn dir={-1}/>
        <NavBtn dir={1}/>

        <div style={{ overflow:'hidden', borderRadius:12 }}>
          <div style={{
            display:'flex', gap:CARD_GAP,
            transform:`translateX(-${idx * (CARD_W + CARD_GAP)}px)`,
            transition:'transform 0.3s cubic-bezier(0.4,0,0.2,1)',
          }}>
            {referrals.map(r => {
              const p = r.profile || {};
              const photo = (p.photos || [])[0];
              const isLiked = liked.has(r.id);

              return (
                <div
                  key={r.id}
                  onClick={() => setPreviewUser(p)}
                  style={{
                    flexShrink:0, width:CARD_W, height:CARD_H,
                    borderRadius:12, overflow:'hidden', position:'relative',
                    background:'var(--mid)', cursor:'pointer',
                    border:'1px solid var(--border)',
                  }}
                >
                  {/* Background photo */}
                  {photo && (
                    <img src={photo} alt={p.name}
                      style={{ position:'absolute', inset:0, width:'100%', height:'100%', objectFit:'cover' }}
                    />
                  )}

                  {/* Gradient — covers only bottom 40% (88px of 220px) */}
                  <div style={{
                    position:'absolute', inset:0,
                    background:'linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.6) 52px, transparent 88px)',
                  }}/>

                  {/* Compat score badge — top left */}
                  {compats[r.profile_id] != null && (
                    <div style={{
                      position:'absolute', top:7, left:8, zIndex:2,
                      background:'rgba(0,0,0,0.55)',
                      borderRadius:99, padding:'2px 6px',
                      display:'flex', alignItems:'baseline', gap:1,
                    }}>
                      <span style={{ fontSize:'0.62rem', fontWeight:700, color:COMPAT_COLOR(compats[r.profile_id]), letterSpacing:'-0.3px' }}>
                        {compats[r.profile_id]}
                      </span>
                      <span style={{ fontSize:'0.45rem', fontWeight:600, color:COMPAT_COLOR(compats[r.profile_id]) }}>%</span>
                    </div>
                  )}

                  {/* Dismiss */}
                  <button
                    onClick={e => { e.stopPropagation(); dismiss(r.id); }}
                    style={{
                      position:'absolute', top:7, right:7, zIndex:2,
                      width:20, height:20, borderRadius:'50%',
                      background:'rgba(0,0,0,0.45)', border:'none',
                      cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center',
                      color:'rgba(255,255,255,0.8)', fontSize:9,
                    }}
                  >✕</button>

                  {/* Content — name + via + buttons only (≤ 40% of card height) */}
                  <div style={{ position:'absolute', bottom:0, left:0, right:0, padding:'0 10px 10px' }}>
                    <div style={{ fontSize:'1rem', fontWeight:600, color:'#fff', fontFamily:"'Cormorant Garamond',serif", lineHeight:1 }}>
                      {p.name}{p.age ? `, ${p.age}` : ''}
                    </div>
                    <div style={{ fontSize:'0.58rem', color:'#C9A84C', fontWeight:600, marginTop:3, display:'flex', alignItems:'center', gap:4 }}>
                      ✦ via {r.referrer?.name}
                      {r.message && (
                        <svg title={r.message} width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#C9A84C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity:0.75, flexShrink:0 }}>
                          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                          <circle cx="9" cy="11" r="1" fill="#C9A84C" stroke="none"/>
                          <circle cx="12" cy="11" r="1" fill="#C9A84C" stroke="none"/>
                          <circle cx="15" cy="11" r="1" fill="#C9A84C" stroke="none"/>
                        </svg>
                      )}
                    </div>

                    {/* Action buttons */}
                    <div style={{ display:'flex', gap:5, marginTop:7 }} onClick={e => e.stopPropagation()}>
                      {/* Like suggested profile */}
                      <button
                        onClick={() => { likeUser?.(p.id); setLiked(prev => new Set([...prev, r.id])); }}
                        disabled={isLiked}
                        title={fr ? 'Aimer' : 'Like'}
                        style={{
                          flex:1, padding:'4px 0', borderRadius:6,
                          background: isLiked ? 'rgba(201,168,76,0.2)' : 'rgba(255,255,255,0.1)',
                          border: isLiked ? '1px solid rgba(201,168,76,0.7)' : '1px solid rgba(255,255,255,0.18)',
                          color: isLiked ? '#C9A84C' : '#fff',
                          cursor: isLiked ? 'default' : 'pointer', fontSize:'0.75rem',
                          display:'flex', alignItems:'center', justifyContent:'center',
                        }}
                      >
                        {isLiked ? '♥' : '♡'}
                      </button>

                      {/* Message suggested profile */}
                      <button
                        onClick={() => { dismiss(r.id); setActiveChat(p.id); setPage('messages'); }}
                        title={fr ? 'Écrire' : 'Message'}
                        style={{
                          flex:1, padding:'4px 0', borderRadius:6,
                          background:'rgba(255,255,255,0.1)',
                          border:'1px solid rgba(255,255,255,0.18)',
                          color:'#fff', cursor:'pointer',
                          display:'flex', alignItems:'center', justifyContent:'center',
                        }}
                      >
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                        </svg>
                      </button>

                      {/* Ask the friend who referred */}
                      <button
                        onClick={() => { setActiveChat(r.referrer_id); setPage('messages'); }}
                        title={fr ? 'Écrire à l\'ami(e)' : 'Ask friend'}
                        style={{
                          flex:1, padding:'4px 0', borderRadius:6,
                          background:'rgba(201,168,76,0.12)',
                          border:'1px solid rgba(201,168,76,0.5)',
                          color:'#C9A84C', cursor:'pointer',
                          display:'flex', alignItems:'center', justifyContent:'center',
                        }}
                      >
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                          <polyline points="22,6 12,13 2,6"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Dot indicators */}
      <div style={{ display:'flex', justifyContent:'center', gap:4, marginTop:10 }}>
        {referrals.map((_, i) => (
          <button
            key={i}
            onClick={() => setIdx(i)}
            style={{
              width: i === idx ? 16 : 5, height:5, borderRadius:3,
              border:'none', padding:0, cursor:'pointer',
              background: i === idx ? 'var(--gold)' : 'var(--border)',
              transition:'width 0.2s, background 0.2s',
            }}
          />
        ))}
      </div>

      {previewUser && <ProfileSheet user={previewUser} onClose={() => setPreviewUser(null)}/>}
    </div>
  );
}

// ── Main Messages component ───────────────────────────────────────────────
export default function Messages() {
  const tr = useTr();
  const {
    activeChat, activeMessages, conversations,
    currentUser, featureMatrix, features, matches,
    sendMessage, setActiveChat, markConversationRead,
    users, setPage, lang,
  } = useApp();

  const beeconnectEnabled = features?.beeconnect !== false;

  const [tab,    setTab]    = useState('romantic');  // 'romantic' | 'connections'
  const [search, setSearch] = useState('');

  // conv.partner is pre-resolved by fetchConvs; fall back to global arrays.
  const getPartner = (conv) => {
    const otherId = conv.user_a === currentUser?.id ? conv.user_b : conv.user_a;
    const fromConv = conv.partner?.id ? conv.partner : null;
    const fromUsers = (users || []).find(u => u.id === otherId);
    const fromMatches = (matches || []).find(m => m.id === otherId);
    return { ...(fromUsers || fromMatches || {}), ...(fromConv || {}), id: otherId };
  };

  const getUnread = (conv) => {
    if (!currentUser) return 0;
    return conv.user_a === currentUser.id ? (conv.unread_a || 0) : (conv.unread_b || 0);
  };

  const sortedConvs = (filter) => (conversations || []).filter(cv => {
    const partner = getPartner(cv);
    if (!partner?.id) return false;
    if (filter === 'romantic')    return cv.mode !== 'friendly';
    if (filter === 'connections') return cv.mode === 'friendly';
    return true;
  }).filter(cv => {
    if (!search.trim()) return true;
    return getPartner(cv)?.name?.toLowerCase().includes(search.toLowerCase());
  }).sort((a, b) => {
    const uA = getUnread(a), uB = getUnread(b);
    if (uA !== uB) return uB - uA;
    return (b.last_message_at || '').localeCompare(a.last_message_at || '');
  });

  const romanticConvs    = sortedConvs('romantic');
  const connectionConvs  = sortedConvs('connections');
  const activeConvs      = tab === 'romantic' ? romanticConvs : connectionConvs;

  const romanticUnread    = romanticConvs.reduce((s, cv)   => s + getUnread(cv), 0);
  const connectionUnread  = connectionConvs.reduce((s, cv) => s + getUnread(cv), 0);
  const totalUnread       = romanticUnread + connectionUnread;

  // Active chat
  const activeConv = (conversations || []).find(cv => getPartner(cv)?.id === activeChat);
  const activePartner = activeConv
    ? getPartner(activeConv)
    : activeChat
      ? ((matches || []).find(u => u.id === activeChat) || (users || []).find(u => u.id === activeChat) || null)
      : null;

  if (activeChat && activePartner) {
    return (
      <div style={{ height:'calc(100vh - 64px)', display:'flex', flexDirection:'column', background:'var(--black)' }}>
        <ChatView partner={activePartner} conv={activeConv || null} onBack={() => setActiveChat(null)}/>
      </div>
    );
  }

  return (
    <div style={{ minHeight:'100vh', background:'var(--black)', fontFamily:'Outfit' }}>
      {/* Header */}
      <div style={{ padding:'1rem 1.2rem 0', background:'var(--black)', position:'sticky', top:0, zIndex:10 }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'0.8rem' }}>
          <h1 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.6rem', fontWeight:400, color:'var(--white)', margin:0 }}>
            {tr('nav.messages') || 'Messages'}
            {totalUnread > 0 && (
              <span style={{ marginLeft:8, fontSize:'0.72rem', padding:'1px 8px', borderRadius:99, background:'var(--gold-pale)', border:'1px solid var(--gold-border)', color:'var(--gold)', fontFamily:'Outfit', fontWeight:600 }}>
                {totalUnread}
              </span>
            )}
          </h1>
        </div>

        {/* Tab bar */}
        <div style={{ display:'flex', gap:0, borderBottom:'2px solid var(--border)', marginBottom:'0' }}>
          {[
            { id:'romantic',    label: lang === 'fr' ? '❤ Amour' : '❤ Matches',       color:'var(--gold)',   unread: romanticUnread,   count: romanticConvs.length },
            beeconnectEnabled && { id:'connections', label: lang === 'fr' ? '✦ Connexions' : '✦ Connect', color:'#1D9E75',      unread: connectionUnread, count: connectionConvs.length },
          ].filter(Boolean).map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              flex:1, padding:'0.6rem 0.5rem', background:'transparent', border:'none',
              borderBottom: tab === t.id ? `2px solid ${t.color}` : '2px solid transparent',
              color: tab === t.id ? t.color : 'var(--muted)',
              fontFamily:'Outfit', fontSize:'0.78rem', fontWeight: tab === t.id ? 700 : 400,
              cursor:'pointer', transition:'all 0.15s', display:'flex', alignItems:'center', justifyContent:'center', gap:'0.35rem',
            }}>
              {t.label}
              {t.count > 0 && (
                <span style={{ fontSize:'0.6rem', padding:'1px 6px', borderRadius:99, background: tab === t.id ? `${t.color}20` : 'rgba(255,255,255,0.06)', color: tab === t.id ? t.color : 'var(--muted)', fontWeight:600 }}>
                  {t.count}
                </span>
              )}
              {t.unread > 0 && (
                <span style={{ width:7, height:7, borderRadius:'50%', background: t.color, display:'inline-block' }}/>
              )}
            </button>
          ))}
        </div>

        {/* Search */}
        <div style={{ position:'relative', margin:'0.7rem 0 0.5rem' }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--muted)" strokeWidth="2" style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', pointerEvents:'none' }}>
            <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
          </svg>
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder={tr('common.search') || 'Search…'}
            style={{ width:'100%', padding:'0.45rem 0.75rem 0.45rem 2rem', background:'rgba(255,255,255,0.05)', border:'1px solid var(--border)', borderRadius:99, color:'var(--white)', fontFamily:'Outfit', fontSize:'0.82rem', outline:'none', boxSizing:'border-box' }}/>
        </div>
      </div>

      {/* Friend suggestions moved to Matches page */}

      {/* Conversation list */}
      {activeConvs.length === 0 ? (
        <div style={{ textAlign:'center', padding:'3rem 1.5rem' }}>
          <div style={{ fontSize:'2rem', marginBottom:'0.8rem', color:'var(--muted)' }}>
            {tab === 'connections'
              ? <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--muted)" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg>
              : <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--muted)" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>}
          </div>
          <p style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.2rem', color:'var(--white)', marginBottom:'0.4rem' }}>
            {tab === 'connections'
              ? (lang === 'fr' ? 'Aucune connexion' : 'No connections yet')
              : (lang === 'fr' ? 'Aucun match' : 'No matches yet')}
          </p>
          <p style={{ fontSize:'0.78rem', color:'var(--muted)', lineHeight:1.6 }}>
            {tab === 'connections'
              ? (lang === 'fr' ? 'Connecte-toi avec des gens dans BeeConnect pour discuter.' : 'Connect with people in BeeConnect to start chatting.')
              : (lang === 'fr' ? 'Like quelqu\'un dans Discover pour créer un match.' : 'Like someone back in Discover to create a match.')}
          </p>
          <button onClick={() => setPage(tab === 'connections' ? 'beeconnect' : 'discover')}
            style={{ marginTop:'1rem', padding:'0.65rem 1.5rem', background:'transparent', border:`1px solid ${tab === 'connections' ? 'rgba(29,158,117,0.4)' : 'var(--gold-border)'}`, borderRadius:99, color: tab === 'connections' ? '#1D9E75' : 'var(--gold)', fontSize:'0.82rem', cursor:'pointer', fontFamily:'Outfit' }}>
            {tab === 'connections' ? (lang === 'fr' ? 'Aller à BeeConnect' : 'Go to BeeConnect') : (lang === 'fr' ? 'Découvrir' : 'Go to Discover')}
          </button>
        </div>
      ) : (
        <div>
          {activeConvs.map(conv => {
            const partner = getPartner(conv);
            const unread  = getUnread(conv);
            return (
              <ConvRow
                key={conv.id}
                partner={partner}
                conv={conv}
                unread={unread}
                onClick={() => setActiveChat(partner.id)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
